import { Box } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { hasViewAccessToResource } from "api/general";
import { useImports } from "api/imports";
import { useReward } from "api/rewards";
import { useSegments } from "api/segments";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { has } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { IMPORTS_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ImportListItem, ImportTypes, ImportTypesUnion } from "types/import";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { formatNumberValue } from "utilities/numbers";
import { ImportCsvModal } from "../ImportCsv/ImportCsvModal/ImportCsvModal";
import { ImportTransactionModal } from "../Transaction/TransactionList/ImportTransactionModal";
import { ImportUnitsTransfersModal } from "../Unit/UnitTransfersList/ImportUnitsTransfersModal";
import { ImportXmlModal } from "./ImportXmlModal";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    warnIcon: {
      color: theme.orange500,
      height: "15px",
      opacity: 1,
      width: "15px",
    },
    tableRow: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(6),
    },
    infoIcon: {
      color: theme.gray500,

      "& path": {
        strokeWidth: 1.5,
      },
    },
    tooltipTitle: {
      color: theme.gray800,
      fontSize: 14,
      margin: theme.spacing(0, 0, 2),
    },
    tooltipDesc: {
      color: theme.gray500,
      fontSize: 12,
      fontWeigh: 400,
      margin: 0,
    },
  }),
);

interface Props {
  activeTab: string;
  tabLabels: {
    readonly massActionsLog?: string | undefined;
    readonly exports?: string | undefined;
    readonly imports?: string | undefined;
  };
}

export const ImportsList = ({ activeTab, tabLabels }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { listing, updateListing } = useListing(IMPORTS_LIST_ID);
  const appDispatch = useAppDispatch();
  const tables = useAppSelector(selectTables);
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[IMPORTS_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      IMPORTS_LIST_ID,
    );

    setFilters(filters);
  };
  const { useImportsList } = useImports();
  const { importSegmentMembers } = useSegments();
  const { importCoupons } = useReward();
  const hasViewImportPermission = hasViewAccessToResource("IMPORT");

  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const [importType, setImportType] = useState<ImportTypesUnion>();
  const [isImportMembersModalOpen, setImportMembersModalOpen] = useState(false);
  const [isImportSegmentMembersModalOpen, setImportSegmentMembersModalOpen] =
    useState(false);
  const [isImportTransactionModalOpen, setImportTransactionModalOpen] =
    useState(false);

  const { data, error } = useImportsList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "uploadedDate",
    dynamicSearch,
  });

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, IMPORTS_LIST_ID);
    }
    setPersistentFilters(filters, IMPORTS_LIST_ID);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, data]);

  const {
    MEMBER,
    GROUP_VALUE,
    SEGMENT_MEMBERS,
    TRANSACTION,
    UNIT_TRANSFER_ADDING,
    UNIT_TRANSFER_SPENDING,
    REWARD_COUPON,
  } = ImportTypes;

  const prepareImportType = (type: ImportTypesUnion, rewardName?: string) => {
    switch (type) {
      case MEMBER:
        return message({ id: "members" });
      case GROUP_VALUE:
        return message({ id: "collections" });
      case SEGMENT_MEMBERS:
        return message({ id: "segmentMembers" });
      case UNIT_TRANSFER_ADDING:
        return message({ id: "addingUnitsTransfers" });
      case UNIT_TRANSFER_SPENDING:
        return message({ id: "deductingUnitsTransfers" });
      case TRANSACTION:
        return message({ id: "transactions" });
      case REWARD_COUPON:
        return (
          <Box className={classes.tableRow}>
            <span>{message({ id: "coupons" })}</span>
            <TooltipWithIcon
              icon={<InfoCircleIcon className={classes.infoIcon} />}
              text={
                <>
                  <p className={classes.tooltipTitle}>
                    {message({ id: "coupons" })}
                  </p>
                  <p className={classes.tooltipDesc}>{rewardName}</p>
                </>
              }
              isWhite
            />
          </Box>
        );
      default:
        return "";
    }
  };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "importId" }),
      property: "importId",
      isSortable: true,
    },
    {
      headerText: message({ id: "importFileName" }),
      property: "fileName",
      isSortable: true,
    },
    {
      headerText: message({ id: "importStartDate" }),
      property: "uploadedDate",
      isSortable: true,
    },
    {
      headerText: message({ id: "importType" }),
      property: "type",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "numberOfRecords" }),
      property: "itemsToImport",
      isSortable: true,
    },
  ]);

  const getFileName = (item: ImportListItem) => {
    if (has(item, "errorMessage")) {
      return (
        <div className={classes.tableRow}>
          {
            <TooltipWithIcon
              className={classes.warnIcon}
              icon={<AlertTriangleIcon />}
              isWhite
              placement="bottom-start"
              text={
                <ExternalLinkTooltipContent text={item.errorMessage || ""} />
              }
            />
          }
          {item.fileName}
        </div>
      );
    } else {
      return item.fileName;
    }
  };

  const createItems = (items: ImportListItem[]): Row[] => {
    return items.map((item) => ({
      cells: [
        { content: item.importId, property: "importId", dataHjSuppress: true },
        {
          content: getFileName(item),
          property: "fileName",
          dataHjSuppress: true,
        },
        {
          content: OLDate(item.uploadedDate),
          property: "uploadedDate",
          dataHjSuppress: true,
        },
        {
          content: prepareImportType(
            item.type,
            item.additionalData?.rewardName,
          ),
          property: "type",
          dataHjSuppress: true,
        },
        {
          align: "right",
          content: formatNumberValue(item.itemsToImport),
          property: "itemsToImport",
          dataHjSuppress: true,
        },
      ],
      id: item.importId,
    }));
  };
  const items = data?.items ? createItems(data.items) : [];

  return (
    <>
      <Subheader>
        <FiltersV2
          data-test-id="imports-list"
          donutLabel={message({ id: "imports" })}
          properties={[
            {
              query: "importId",
              label: message({ id: "importId" }),
              type: "text",
            },
            {
              query: "uploadedDate",
              label: message({ id: "importStartDate" }),
              type: "datetime",
            },
            {
              query: "type",
              label: message({ id: "type" }),
              type: "import-type",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
        />
        <SubheaderMenuButton
          text={message({ id: "import" })}
          menuItems={[
            {
              onClick: () => {
                setImportType(REWARD_COUPON);
              },
              text: message({ id: "coupons" }),
            },
            {
              onClick: () => {
                setImportMembersModalOpen(true);
                setImportType(MEMBER);
              },
              text: message({ id: "members" }),
            },
            {
              onClick: () => {
                setImportTransactionModalOpen(true);
                setImportType(TRANSACTION);
              },
              text: message({ id: "transactions" }),
            },
            {
              onClick: () => {
                setImportSegmentMembersModalOpen(true);
                setImportType(SEGMENT_MEMBERS);
              },
              text: message({ id: "membersToSegment" }),
            },
            {
              onClick: () => {
                setImportType(UNIT_TRANSFER_ADDING);
              },
              text: message(
                { id: "unitsTransfersType" },
                {
                  importType: message({
                    id: "add",
                  }).toLocaleLowerCase(),
                },
              ),
            },
            {
              onClick: () => {
                setImportType(UNIT_TRANSFER_SPENDING);
              },
              text: message(
                { id: "unitsTransfersType" },
                {
                  importType: message({
                    id: "deduct",
                  }).toLocaleLowerCase(),
                },
              ),
            },
          ]}
          disabled={activeTab === tabLabels.imports && hasViewImportPermission}
        />
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="type"
            properties={[{ id: "type", label: "type" }]}
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: IMPORTS_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={IMPORTS_LIST_ID}
        data-test-id="imports"
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                const itemType = data?.items.find(
                  (item: ImportListItem) => item.importId === id,
                )?.type;

                navigate(links.importDetails(itemType!, id as string));
              },
              label: message({ id: "seeImportedRecords" }),
            },
          ],
        }}
        columns={data && headItems}
        onRowClick={(id: string) => {
          const itemType = data?.items.find(
            (item: ImportListItem) => item.importId === id,
          )?.type;

          navigate(links.importDetails(itemType!, id as string));
        }}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={data && items}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "uploadedDate",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        clearFilters={() => setFilters([])}
      />
      <ImportXmlModal
        isModalOpen={isImportMembersModalOpen}
        closeModal={() => setImportMembersModalOpen(false)}
        type={importType}
      />
      <ImportCsvModal
        headerMessage="importMembers"
        dropdown
        identificationFactor
        importType={importType}
        isModalOpen={isImportSegmentMembersModalOpen}
        importData={importSegmentMembers}
        closeModal={() => setImportSegmentMembersModalOpen(false)}
      />
      <ImportUnitsTransfersModal
        type={UNIT_TRANSFER_ADDING}
        isModalOpen={importType === UNIT_TRANSFER_ADDING}
        closeModal={() => setImportType(undefined)}
        setModal={() => setImportType(UNIT_TRANSFER_SPENDING)}
      />
      <ImportUnitsTransfersModal
        type={UNIT_TRANSFER_SPENDING}
        isModalOpen={importType === UNIT_TRANSFER_SPENDING}
        closeModal={() => setImportType(undefined)}
        setModal={() => setImportType(UNIT_TRANSFER_ADDING)}
      />
      <ImportTransactionModal
        isModalOpen={isImportTransactionModalOpen}
        closeModal={() => setImportTransactionModalOpen(false)}
      />
      <ImportCsvModal
        headerMessage="importCoupons"
        dropdown
        importType={importType}
        isModalOpen={importType === REWARD_COUPON}
        importData={importCoupons}
        closeModal={() => setImportType(undefined)}
      />
    </>
  );
};
